import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Avatar,
    useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { Search, MonetizationOn, People, Receipt } from '@mui/icons-material';

const dummyPayments = [
    { id: 1, user: { name: 'John Doe', avatar: 'J' }, amount: 50.00, status: 'Completed', date: '2023-10-26' },
    { id: 2, user: { name: 'Jane Smith', avatar: 'J' }, amount: 25.50, status: 'Pending', date: '2023-10-25' },
    { id: 3, user: { name: 'Peter Jones', avatar: 'P' }, amount: 100.00, status: 'Completed', date: '2023-10-24' },
    { id: 4, user: { name: 'Mary Johnson', avatar: 'M' }, amount: 75.00, status: 'Failed', date: '2023-10-23' },
    { id: 5, user: { name: 'Chris Lee', avatar: 'C' }, amount: 10.00, status: 'Completed', date: '2023-10-22' },
    { id: 6, user: { name: 'Patricia Brown', avatar: 'P' }, amount: 300.00, status: 'Completed', date: '2023-10-21' },
    { id: 7, user: { name: 'Robert Williams', avatar: 'R' }, amount: 15.00, status: 'Pending', date: '2023-10-20' },
];

const StatCard = ({ title, value, icon, color }) => (
    <Paper elevation={3} sx={{ p: 3, display: 'flex', alignItems: 'center', borderRadius: '12px' }}>
        <Avatar sx={{ bgcolor: `${color}.main`, mr: 2, width: 56, height: 56 }}>{icon}</Avatar>
        <Box>
            <Typography variant="h6" color="text.secondary">{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
        </Box>
    </Paper>
);


const PaymentPage = () => {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const filteredPayments = useMemo(() => {
        return dummyPayments.filter(payment =>
            (payment.user.name.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (statusFilter === '' || payment.status === statusFilter)
        );
    }, [searchTerm, statusFilter]);

    const totalRevenue = useMemo(() => {
        return dummyPayments
            .filter(p => p.status === 'Completed')
            .reduce((acc, p) => acc + p.amount, 0)
            .toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }, []);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const statusColors = {
        Completed: 'success',
        Pending: 'warning',
        Failed: 'error',
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Box>
                <Typography variant="h4" sx={{ 
                    fontWeight: 'bold', 
                    mb: 4,
                    fontSize: { xs: '1.2rem', sm: '1.8rem' }
                }}>Payments Overview</Typography>

                <Grid container spacing={{ xs: 2, sm: 4 }} mb={4}>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard title="Total Revenue" value={totalRevenue} icon={<MonetizationOn />} color="success" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard title="Transactions" value={dummyPayments.length} icon={<Receipt />} color="info" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard title="Customers" value={new Set(dummyPayments.map(p => p.user.name)).size} icon={<People />} color="primary" />
                    </Grid>
                </Grid>

                <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        mb: 2, 
                        flexWrap: 'wrap', 
                        gap: 2 
                    }}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search payments..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ 
                                minWidth: { xs: '100%', sm: '300px' },
                                width: { xs: '100%', sm: 'auto' }
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                                sx: { borderRadius: '8px' }
                            }}
                        />
                        <FormControl size="small" sx={{ 
                            minWidth: { xs: '100%', sm: 120 }, 
                            width: { xs: '100%', sm: 'auto' },
                            borderRadius: '8px' 
                        }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Failed">Failed</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ 
                                    backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black'
                                }}>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                        borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                    }}>User</TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                        borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                    }}>Amount</TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                        borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                    }}>Status</TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                        borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                    }}>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>{payment.user.avatar}</Avatar>
                                                <Typography variant="body2">{payment.user.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>${payment.amount.toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Chip label={payment.status} color={statusColors[payment.status]} size="small" />
                                        </TableCell>
                                        <TableCell>{payment.date}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredPayments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </motion.div>
    );
};

export default PaymentPage; 